import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Camera, Save, Eye, EyeOff, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import CameraPhotoUpload from '../components/CameraPhotoUpload';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { formatErrorMessage } from '../utils/formatError';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Profile = () => {
  const { isMobile, isTablet } = useDeviceDetection();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [mobileError, setMobileError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_number: '',
    default_location: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [profilePhoto, setProfilePhoto] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Format and validate mobile number
  const formatMobileNumber = (value) => {
    // Remove all non-digit characters except + at the beginning
    let cleaned = value.replace(/[^\d+]/g, '');
    
    // If starts with +, keep it, otherwise remove any +
    if (!cleaned.startsWith('+')) {
      cleaned = cleaned.replace(/\+/g, '');
    }
    
    return cleaned;
  };

  const validateMobileNumber = (value) => {
    if (!value || value.trim() === '') {
      setMobileError('');
      return true; // Empty is allowed
    }
    
    // Remove formatting for validation
    const cleaned = value.replace(/[\s-]/g, '');
    
    // UAE mobile format: +971 5X XXX XXXX or 05X XXX XXXX
    // International format: +XXX followed by digits (7-15 total digits)
    const uaePattern = /^(\+971|971|0)?5[0-9]{8}$/;
    const internationalPattern = /^\+?[1-9]\d{6,14}$/;
    
    if (uaePattern.test(cleaned) || internationalPattern.test(cleaned)) {
      setMobileError('');
      return true;
    } else {
      setMobileError('Please enter a valid mobile number (e.g., +971 50 123 4567)');
      return false;
    }
  };

  const handleMobileChange = (e) => {
    const value = e.target.value;
    // Only allow digits, +, spaces, and hyphens
    const filtered = value.replace(/[^0-9+\s-]/g, '');
    setFormData({ ...formData, mobile_number: filtered });
    
    // Validate on change (but allow typing)
    if (filtered.length > 5) {
      validateMobileNumber(filtered);
    } else {
      setMobileError('');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/swap-locations`, { headers: getAuthHeader() });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/profile`, { headers: getAuthHeader() });
      setUser(response.data);
      setFormData({
        name: response.data.name || '',
        email: response.data.email || '',
        mobile_number: response.data.mobile_number || '',
        default_location: response.data.default_location || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setProfilePhoto(response.data.profile_photo || '');
      setPhotoPreview(response.data.profile_photo || '');
      setLoading(false);
    } catch (error) {
      console.error('Profile fetch error:', error);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchLocations();
  }, []);

  // Function to crop image to centered square
  const cropImageToSquare = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        // Calculate the square crop dimensions (center crop)
        const size = Math.min(img.width, img.height);
        const offsetX = (img.width - size) / 2;
        const offsetY = (img.height - size) / 2;
        
        // Set canvas to output size (e.g., 400x400 for profile photos)
        const outputSize = 400;
        canvas.width = outputSize;
        canvas.height = outputSize;
        
        // Draw cropped and scaled image
        ctx.drawImage(
          img,
          offsetX, offsetY, size, size, // Source rectangle (cropped)
          0, 0, outputSize, outputSize   // Destination rectangle (scaled)
        );
        
        // Convert to base64
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
      
      // Read file as data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoUpload = async (file) => {
    try {
      // Crop the image to a centered square before uploading
      const croppedBase64 = await cropImageToSquare(file);
      setPhotoPreview(croppedBase64);
      
      await axios.post(
        `${API}/api/profile/photo`,
        { photo: croppedBase64 },
        { headers: getAuthHeader() }
      );
      toast.success('Profile photo updated successfully!');
      setProfilePhoto(croppedBase64);
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate mobile number
    if (formData.mobile_number && !validateMobileNumber(formData.mobile_number)) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    // Validate password fields
    if (formData.new_password) {
      if (!formData.current_password) {
        toast.error('Please enter your current password to change password');
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error('New passwords do not match');
        return;
      }
      if (formData.new_password.length < 6) {
        toast.error('New password must be at least 6 characters');
        return;
      }
    }

    try {
      // Send name, mobile_number, and default_location (email and role are read-only)
      // Convert "none" to empty string for no default location
      const defaultLocation = formData.default_location === 'none' ? '' : formData.default_location;
      const updateData = {
        name: formData.name,
        mobile_number: formData.mobile_number,
        default_location: defaultLocation
      };

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      await axios.put(`${API}/api/profile`, updateData, { headers: getAuthHeader() });
      toast.success('Profile updated successfully!');
      
      // Update localStorage with new default location for immediate use
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      currentUser.default_location = defaultLocation;
      localStorage.setItem('user', JSON.stringify(currentUser));
      
      // Clear password fields
      setFormData({
        ...formData,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      fetchProfile();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update profile'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">My Profile</h1>
        <p className="text-sm sm:text-base text-slate-600 mt-1">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Profile Photo Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Profile Photo
            </CardTitle>
            <CardDescription>Upload or take a photo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-full bg-slate-100 flex items-center justify-center mb-4 overflow-hidden border-4 border-lime-600">
                {photoPreview ? (
                  <img 
                    src={photoPreview} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-16 h-16 text-slate-400" />
                )}
              </div>
              <CameraPhotoUpload onUpload={handlePhotoUpload} currentPhoto={photoPreview} />
            </div>
          </CardContent>
        </Card>

        {/* Profile Information Card */}
        <Card className="border-0 shadow-lg lg:col-span-2">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Update your personal details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Address (Fixed)</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-slate-500">Contact admin to change email</p>
                </div>
                <div className="space-y-2">
                  <Label>Mobile Number</Label>
                  <Input
                    type="tel"
                    value={formData.mobile_number}
                    onChange={handleMobileChange}
                    onBlur={() => validateMobileNumber(formData.mobile_number)}
                    placeholder="+971 50 123 4567"
                    className={mobileError ? 'border-red-500 focus:ring-red-500' : ''}
                    maxLength={20}
                  />
                  {mobileError ? (
                    <p className="text-xs text-red-500">{mobileError}</p>
                  ) : (
                    <p className="text-xs text-slate-500">Enter UAE or international mobile number</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Role (Fixed)</Label>
                  <Input
                    value={user?.role || ''}
                    disabled
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Default Location Section */}
              <div className="border-t pt-4 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold">Default Working Location</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Set your default location for Battery Swaps and Maintenance transactions. 
                  You can still select a different location when creating new transactions.
                </p>
                <div className="max-w-md">
                  <Label>Default Location</Label>
                  <Select
                    value={formData.default_location}
                    onValueChange={(value) => setFormData({ ...formData, default_location: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select your default location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No default location</SelectItem>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.name}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.default_location && formData.default_location !== 'none' && (
                    <p className="text-xs text-green-600 mt-2">
                      ✓ Your default location is set to: <strong>{formData.default_location}</strong>
                    </p>
                  )}
                </div>
              </div>

              {/* Password Change Section */}
              <div className="border-t pt-4 mt-6">
                <h3 className="text-lg font-semibold mb-4">Change Password</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Current Password</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        value={formData.current_password}
                        onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                        placeholder="Enter current password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={formData.new_password}
                        onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                        placeholder="Enter new password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirm_password}
                        onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                        placeholder="Confirm new password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Leave password fields empty if you don't want to change your password
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button type="submit" className="bg-gradient-to-r from-lime-600 to-green-600">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
